import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ShoppingBag } from "lucide-react";

import { openExternal } from "@/lib/api";
import { ProductPlaceholder } from "@/components/cards";
import { Button, Chip } from "@/components/kit";
import { useShop } from "@/shop";

export function formatMoney(m) {
  if (!m) return "";
  const n = Number(m.amount).toFixed(2);
  return m.currencyCode === "AUD" ? `A$${n}` : `${m.currencyCode} ${n}`;
}

export function firstAvailable(p) {
  return p.variants.find((v) => v.available) ?? p.variants[0];
}

export function LiveImage({ live, name, height }) {
  if (!live?.image) return <ProductPlaceholder name={name} height={height} />;
  return <img src={live.image} alt={live.title || name} loading="lazy" className="w-full rounded-xl bg-tert object-cover" style={{ height }} />;
}

export function PriceTag({ variant, testId, size = "md" }) {
  if (!variant) return <span data-testid={testId} className="font-body-semi text-[13px] text-mute">Live price unavailable — view on breez.au</span>;
  const compare = variant.compare_at_price && Number(variant.compare_at_price.amount) > Number(variant.price.amount) ? variant.compare_at_price : null;
  const fs = size === "lg" ? "text-[26px]" : size === "sm" ? "text-sm" : "text-[17px]";
  return (
    <span className="flex items-baseline gap-2">
      <span data-testid={testId} className={`font-display-bold text-ink ${fs}`}>{formatMoney(variant.price)}</span>
      {compare ? <span className="font-body-medium text-[13px] text-mute line-through">{formatMoney(compare)}</span> : null}
    </span>
  );
}

export function StockBadge({ variant, testId }) {
  if (!variant) return null;
  const q = variant.quantity_available;
  const label = !variant.available ? "Sold out" : q != null && q > 0 && q <= 5 ? `Only ${q} left` : "In stock";
  const color = !variant.available ? "#EF4444" : q != null && q <= 5 ? "#F59E0B" : "#10B981";
  return (
    <span data-testid={testId} className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="font-body-bold text-xs" style={{ color }}>{label}</span>
    </span>
  );
}

export function VariantPicker({ live, value, onChange, testPrefix }) {
  if (!live.has_options) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {live.variants.map((v) => (
        <Chip key={v.id} testId={`${testPrefix}-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} label={`${v.title}${v.available ? "" : " · sold out"}`} selected={value === v.id} onClick={() => onChange(v.id)} />
      ))}
    </div>
  );
}

/** Live product row: real image, price, variants, stock, Shop now + Add to cart. */
export function LiveBuyBox({ slug, testPrefix, compact }) {
  const { live, addToCart, cartBusy, catalog } = useShop();
  const lp = live(slug);
  const [variantId, setVariantId] = useState(undefined);
  const v = lp ? lp.variants.find((x) => x.id === variantId) ?? firstAvailable(lp) : undefined;
  if (!lp) {
    return (
      <div className="flex flex-col gap-2">
        <span data-testid={`${testPrefix}-offline`} className="font-body-semi text-[13px] text-mute">
          {catalog && !catalog.live ? "Live prices unavailable — view on breez.au" : "Loading live price…"}
        </span>
        <Button testId={`${testPrefix}-shop-now`} title="Shop now" variant="outline" icon={ExternalLink} onClick={() => openExternal("https://breez.au")} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PriceTag variant={v} testId={`${testPrefix}-price`} />
        <StockBadge variant={v} testId={`${testPrefix}-stock`} />
      </div>
      {!compact ? <VariantPicker live={lp} value={v?.id} onChange={setVariantId} testPrefix={`${testPrefix}-variant`} /> : null}
      <div className={`flex gap-2 ${compact ? "flex-col" : "flex-row"}`}>
        {!compact ? <Button testId={`${testPrefix}-shop-now`} title="Shop now" variant="outline" onClick={() => openExternal(lp.url)} className="flex-1 !px-3" /> : null}
        <Button
          testId={`${testPrefix}-add-to-cart`}
          title={v?.available ? "Add" : "Sold out"}
          icon={v?.available ? ShoppingBag : undefined}
          disabled={!v?.available || cartBusy}
          onClick={() => v && addToCart([{ variant_id: v.id, quantity: 1 }])}
          className={`${compact ? "" : "flex-1"} !px-3`}
        />
      </div>
    </div>
  );
}

/** "Add my routine to cart": primary + supporting product (first available variants). */
export function AddRoutineButton({ slugs, testId }) {
  const { live, addToCart, cartBusy } = useShop();
  const variants = slugs.map((s) => live(s)).filter(Boolean).map((p) => firstAvailable(p)).filter((v) => v?.available);
  if (!variants.length) return null;
  return (
    <Button
      testId={testId}
      title={variants.length > 1 ? "Add routine to cart" : "Add to cart"}
      icon={ShoppingBag}
      disabled={cartBusy}
      onClick={() => addToCart(variants.map((v) => ({ variant_id: v.id, quantity: 1 })))}
      className="w-full"
    />
  );
}

export function CartIconButton({ testId }) {
  const { cart } = useShop();
  const navigate = useNavigate();
  const n = cart?.total_quantity ?? 0;
  return (
    <div className="relative">
      <Button testId={testId} title={n ? `Cart · ${n}` : "Cart"} icon={ShoppingBag} variant="outline" onClick={() => navigate("/cart")} className="!min-h-[44px] !px-4" />
      {n ? <span className="absolute right-1.5 top-0.5 h-2.5 w-2.5 rounded-full bg-ocean" /> : null}
    </div>
  );
}
